import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, worksheetQuestions, allowancesQuestionsFromData } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function LegacyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  // By default, it should be displaying the latest form (12_22 which is for 2023)
  // We do not have 2022 California State Tax version (12_21)
  const versionToYear = () => {
    switch (version) {
      case '1_21':
        return '2021';
      default:
        return '2023';
    }
  };

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const [numRegularWithholdingAllowances, setNumRegularWithholdingAllowances] = useState(data.num_regular_withholding_allowances || 0);
  const [numRegularWithholdingAllowancesError, setNumRegularWithholdingAllowancesError] = useState(false);
  const [numEstimatedDeductionsAllowances, setNumEstimatedDeductionsAllowances] = useState(data.estimated_deductions_allowances || 0);
  const [estimatedDeductionsError, setEstimatedDeductionsError] = useState(false);

  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const worksheetForm = useRef();
  const worksheetInitialQuestions = getInitialQuestions(worksheetQuestions);
  const [wsQuestions, setWSQuestions] = useState(worksheetInitialQuestions);

  const allowancesInitialQuestions = getInitialQuestions(allowancesQuestionsFromData);
  const [allowancesQuestions, setAllowancesQuestions] = useState(allowancesInitialQuestions);

  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  // to calculate total number of allowances
  const allowancesForDependents = wsQuestions[0].answer.answer.dependents || 0;
  const calculatedNoAllowances = allowancesQuestions.reduce((acc, question) => {
    if (Object.values(question.answer.answer)[0]) acc += 1;
    return acc;
  }, 0);
  const calculatedNumRegularWithholdingAllowances = calculatedNoAllowances + parseInt(allowancesForDependents);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderWorksheetA = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px">
        Number of regular withholding allowances
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={worksheetOpen ? calculatedNumRegularWithholdingAllowances : numRegularWithholdingAllowances}
            onChange={(e) => setNumRegularWithholdingAllowances(e.target.value)}
            disabled={worksheetOpen || isReviewStep}
          />
        </Flex>
      </S.QuestionWrapper>
      {numRegularWithholdingAllowancesError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
      )}
      <S.QuestionLabel>
        Number of allowances from your Estimated Deductions
      </S.QuestionLabel>
      <Text style={{ fontSize: '11px', fontWeight: '400', color: 'grey' }} mb="7px">
        Complete Worksheet B on the California DE 4 to calculate this. Otherwise, leave it as 0.
      </Text>
      <S.QuestionWrapper>
        <Flex>
          <S.Input
            type="text"
            value={numEstimatedDeductionsAllowances}
            onChange={(e) => setNumEstimatedDeductionsAllowances(e.target.value)}
            disabled={isReviewStep}
          />
        </Flex>
      </S.QuestionWrapper>
      {estimatedDeductionsError && (
        <Flex justifyContent="flex-start">
          <Text color="red">Please enter a valid response.</Text>
        </Flex>
      )}
      <Flex mt="10px">
        <CollapsibleQuestions
          title="If you need to calculate your allowances, use the worksheet for regular withholding"
          handleClick={() => {
            if (!isReviewStep) {
              setWorksheetOpen(!worksheetOpen);
            }
          }}
          open={worksheetOpen || isReviewStep}
        >
          <Flex column mb="20px">
            <S.QuestionLabel>
              Select any allowances that apply to you and enter the number of dependents you want to claim:
            </S.QuestionLabel>
            {allowancesQuestions.map((q) => {
              const val = Object.values(q.answer.answer)[0];
              return (
                <label>
                  <Flex alignItems="center">
                    <S.Input
                      type="checkbox"
                      value={val}
                      disabled={isReviewStep}
                      onChange={(e) => {
                        const updatedQuestions = allowancesQuestions.map((ques) => {
                          if (q.id !== ques.id) return ques;
                          const answer = {
                            ...ques.answer,
                            answer: {
                              ...ques.answer.answer,
                              [ques.id]: e.target.checked,
                            },
                          };
                          const updatedQuestion = { ...ques, answer };
                          return updatedQuestion;
                        });
                        setAllowancesQuestions(updatedQuestions);
                      }}
                      checked={val}
                    />
                    <Box ml={1} />
                    {q.title}
                  </Flex>
                </label>
              );
            })}
          </Flex>
          <FormConsumer
            questions={wsQuestions}
            {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
            onQuestionChange={({ question, value }) => {
              const questions = wsQuestions.map((q) => {
                if (q.id !== question.id) return q;
                const answer = {
                  ...q.answer,
                  answer: {
                    ...q.answer.answer,
                    [q.id]: value,
                  },
                };
                const updatedQuestion = { ...q, answer };
                return updatedQuestion;
              });
              setWSQuestions(questions);
            }}
            ref={worksheetForm}
          />
        </CollapsibleQuestions>
      </Flex>
    </Flex>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="10px" isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_one'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column>
              <Text mb="15px">
                I claim exemption from withholding for
                { versionToYear() }
                , and I certify I meet both of the conditions for exemption:
              </Text>
              <Text mb="8px">1. You did not owe any federal/state income tax last year.</Text>
              <Text>2. You do not expect to owe any federal/state income tax this year.</Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_two'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>I certify under penalty of perjury that I am not subject to California withholding. I meet the conditions set forth under the Service Member Civil Relief Act, as amended by the Military Spouses Residency Relief Act and the Veterans Benefits and Transition Act of 2018.</Text>
            </Flex>
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
          <Flex justifyContent="flex-start" mt={1}>
            <Text color="red">This is a mandatory field.</Text>
          </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderWorksheetA()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (!isExemptFromWithholding) {
              if (!(/^\d*$/.test(numEstimatedDeductionsAllowances))) {
                err = true;
                setEstimatedDeductionsError(true);
              } else {
                setEstimatedDeductionsError(false);
              }
              if (!(/^\d*$/.test(numRegularWithholdingAllowances))) {
                err = true;
                setNumRegularWithholdingAllowancesError(true);
              } else {
                setNumRegularWithholdingAllowancesError(false);
              }
            } else if (exemptionsFromWithholding === '') {
              err = true;
              setExemptionsError(true);
            } else {
              setExemptionsError(false);
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            const allowancesData = allowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let wsData;
            if (worksheetOpen) {
              wsData = wsQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            } else {
              wsData = worksheetInitialQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            let numRegularWithholdingAllowancesData = '0';
            let aToE = '0';
            let totalNumberOfAllowancesData = '0';
            let numEstimatedDeductionsAllowancesTemp = '0';
            if (numEstimatedDeductionsAllowances !== '') numEstimatedDeductionsAllowancesTemp = numEstimatedDeductionsAllowances;
            let exemptionTemp = '';
            if (!isExemptFromWithholding) {
              let numRegularWithholdingAllowancesTemp = '0';
              if (numRegularWithholdingAllowances !== '') numRegularWithholdingAllowancesTemp = numRegularWithholdingAllowances;
              numRegularWithholdingAllowancesData = worksheetOpen ? calculatedNumRegularWithholdingAllowances : numRegularWithholdingAllowancesTemp;
              aToE = worksheetOpen ? calculatedNumRegularWithholdingAllowances : 0;
              const totalNumberOfAllowances = parseInt(numRegularWithholdingAllowancesData) + parseInt(numEstimatedDeductionsAllowancesTemp);
              totalNumberOfAllowancesData = isNaN(totalNumberOfAllowances) ? 0 : totalNumberOfAllowances;
            } else {
              exemptionTemp = exemptionsFromWithholding;
            }
            const allAllowancesData = {
              num_regular_withholding_allowances: numRegularWithholdingAllowancesData.toString(),
              estimated_deductions_allowances: numEstimatedDeductionsAllowancesTemp.toString(),
              total_a_to_e: aToE.toString(),
              total_number_of_allowances: totalNumberOfAllowancesData.toString(),
            };
            const exemptionData = { exemption: exemptionTemp, is_exempt: isExemptFromWithholding };
            const data = {
              ...stepOneData, ...wsData, ...allowancesData, ...allAllowancesData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderWorksheetA(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default LegacyForm;
